<template>
  <div class="c-video-background">
    <!-- `muted` is a static attribute on purpose: browsers evaluate the muted-autoplay decision when the
         element is inserted, and Vue's `:muted` binding does not reliably reflect onto <video>, so a
         late (onMounted) assignment leaves autoplay blocked. -->
    <video ref="videoEl" class="c-video-background__video" :src="src" :poster="poster || undefined" autoplay loop muted playsinline />
  </div>
</template>

<script setup lang="ts">
/**
 * Autoplaying, looping background video. Only the props the app actually uses are kept. The wrapper
 * element receives the caller's sizing classes; the video covers it. Registered globally
 * (components/Global) as `<video-background>` so existing template usages keep resolving.
 */
const props = defineProps({
  src: { type: String, required: true },
  muted: { type: Boolean, default: true },
  poster: { type: String, default: '' },
})

const videoEl = ref<HTMLVideoElement | null>(null)

onMounted(() => {
  if (!videoEl.value) {
    return
  }
  // The template keeps it muted for autoplay; reflect the prop afterwards for the rare unmuted case.
  videoEl.value.muted = props.muted
  // Some browsers need an explicit play() even with the autoplay attribute present.
  videoEl.value.play().catch(() => {})
})
watch(
  () => props.muted,
  (m) => {
    if (videoEl.value) {
      videoEl.value.muted = m
    }
  },
)
</script>

<style scoped lang="scss">
.c-video-background {
  position: relative;
  overflow: hidden;
}

.c-video-background__video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style>
